import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, Clock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const AlertsBell = () => {
  const { company } = useAuth();
  const [data, setData] = useState({ alerts: [], unread: 0 });
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    if (company?.suspended) return;
    api.get("/alerts").then(({ data: d }) => setData(d)).catch(() => {});
  }, [company?.suspended]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const ackAll = async () => {
    await api.post("/alerts/ack-all");
    load();
  };

  const ack = async (id) => {
    await api.post(`/alerts/${id}/ack`);
    load();
  };

  return (
    <div className="relative">
      <button data-testid="alerts-bell" onClick={() => setOpen(!open)}
        className="relative z-50 flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-ink-700 hover:text-gold">
        <Bell className="h-4 w-4" />
        {data.unread > 0 && (
          <span data-testid="alerts-unread-count"
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-400 px-1 font-mono text-[9px] font-bold text-ink-900">
            {data.unread}
          </span>
        )}
      </button>

      {open && (
        <>
        <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
        <div data-testid="alerts-panel"
          className="fixed left-3 right-3 top-16 z-50 w-auto rounded-xl border border-ink-600 bg-ink-800 p-4 shadow-2xl sm:absolute sm:left-auto sm:right-0 sm:top-11 sm:w-[22rem]">
          <div className="flex items-center justify-between">
            <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Dispatch alerts</p>
            {data.unread > 0 && (
              <button data-testid="alerts-ack-all" onClick={ackAll}
                className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-slate-400 hover:text-gold">
                <Check className="h-3 w-3" /> Mark all read
              </button>
            )}
          </div>
          <div className="mt-4 max-h-80 space-y-3 overflow-y-auto">
            {data.alerts.length === 0 && (
              <p data-testid="alerts-empty" className="py-6 text-center text-xs text-slate-500">
                Nothing needs your attention right now.
              </p>
            )}
            {data.alerts.map((a) => (
              <Link key={a.id} to={a.manifest_id ? `/app/manifests/${a.manifest_id}` : "/app/bookings"}
                onClick={() => { setOpen(false); if (!a.acked_at) ack(a.id); }}
                data-testid={`alert-${a.id}`}
                className={`block rounded-lg border p-3 transition-colors ${a.acked_at
                  ? "border-ink-700 bg-ink-900/40 hover:border-gold/30"
                  : "border-amber-500/30 bg-amber-500/5 hover:border-gold/40"}`}>
                <p className="flex items-start gap-2 text-sm text-slate-100">
                  <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-300" /> {a.title}
                </p>
                <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{a.body}</p>
                <p className="mt-2 flex items-center justify-between gap-3 font-mono text-[10px] uppercase tracking-widest text-slate-600">
                  <span>
                    {new Date(a.created_at).toLocaleString([], {
                      month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    {a.email_sent && a.sms_sent
                      ? " · emailed and texted to dispatch"
                      : a.email_sent
                      ? " · emailed to dispatch"
                      : a.sms_sent
                      ? " · texted to dispatch"
                      : " · in-app only"}
                  </span>
                  <span className="text-gold">
                    {a.manifest_number ? `Open trip #${a.manifest_number}` : "Open"}
                  </span>
                </p>
              </Link>
            ))}
          </div>
        </div>
        </>
      )}
    </div>
  );
};
